import { JSX } from 'solid-js';
import { useSearchParams } from '@solidjs/router';
import FlagGreen from '@bz/wallet/src/assets/Flag-green.svg';
import FlagPurple from '@bz/wallet/src/assets/Flag-purple.svg';

import { formatNumber } from '../../../utils';
import Box from '../../ui/Box';
import { DepositMethod, WithdrawMethod } from '../../../types';
import Empty from '../../ui/Empty';
import ToggleList from '../../ui/ToggleList';

// 定义联合类型来处理不同的API响应
type WithdrawResponse = ApiSchemas['WithdrawMethodsResponse'];
type DepositResponse = ApiSchemas['DepositMethodsResponse'];
type PaymentResponse = WithdrawResponse | DepositResponse;
type PaymentMethod = DepositMethod | WithdrawMethod;

// 基础属性接口
interface BasePaymentProps {
  header?: JSX.Element;
  currency: string;
}

// 提现场景的属性接口
interface WithdrawPaymentProps extends BasePaymentProps {
  children: (
    method: WithdrawMethod,
    onBack: () => void,
    groupedMethods: () => Record<string, PaymentMethod[]>,
    selectMethod: (m: PaymentMethod) => void
  ) => JSX.Element;
  isWithdraw: true;
}

// 充值场景的属性接口
interface DepositPaymentProps extends BasePaymentProps {
  children: (
    method: DepositMethod,
    onBack: () => void,
    groupedMethods: () => Record<string, PaymentMethod[]>,
    selectMethod: (m: PaymentMethod) => void
  ) => JSX.Element;
  isWithdraw?: false;
}

// 联合类型，根据 isWithdraw 值确定具体类型
type PaymentProps = WithdrawPaymentProps | DepositPaymentProps;

function Payment(props: PaymentProps) {
  const data = createAsync(async (): Promise<PaymentResponse> => {
    try {
      if (props.isWithdraw) {
        return await get.getWalletCashierGetWithdrawMethods({ currency: props.currency });
      } else {
        return await get.getDepositMethodsByCurrency({
          currency: props.currency,
        });
      }
    } catch (error) {
      console.error('Failed to fetch payment methods:', error);
      throw error;
    }
  });

  const [method, setMethod] = createSignal<PaymentMethod | null>(null);
  const [isManualList, setIsManualList] = createSignal(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // 当 currency 变化时，重置 method
  createEffect(() => {
    if (props.currency) {
      setMethod(null);
      setIsManualList(false);
    }
  });
  let ref: HTMLDivElement | undefined;
  // 当 method 变化时，滚动到 method 所在的位置
  createEffect(() => {
    // 问题：从其他页面到fiat页面, 切换payment就会执行下面的滚动，会有抖动
    // 这个window.scrollY !== 0 是一个偷懒写法，最好是获取ref是否在视野中
    if (method() && window.scrollY !== 0) {
      ref?.scrollIntoView({ block: 'center' });
    }
  });

  // 使用 createMemo 来缓存分组数据
  const groupedMethods = createMemo(() => {
    const response = data();
    if (!response) return {};

    const list = props.isWithdraw
      ? (response as WithdrawResponse).withdrawMethodsByCurrency
      : (response as DepositResponse).depositMethodsByCurrency;

    return list;
  });

  // 寻找最近使用的渠道
  createEffect(() => {
    const response = data();
    const currentGroups = groupedMethods();
    const groupKeys = Object.keys(currentGroups);

    // Only auto-select when not manually switching list and method is empty (for withdrawal, if URL has channelId, it will be selected by effect below)
    const pendingChannel = props.isWithdraw ? searchParams.channelId : undefined;
    if (
      response &&
      groupKeys.length > 0 &&
      !isManualList() &&
      !method() &&
      !(pendingChannel !== undefined && pendingChannel !== null && pendingChannel !== '')
    ) {
      const list = props.isWithdraw
        ? (response as WithdrawResponse).withdrawMethodsByCurrency
        : (response as DepositResponse).depositMethodsByCurrency;

      // 遍历所有分类寻找 tag === 0 (Last Used) 的渠道
      let lastUsed: PaymentMethod | undefined;
      Object.values(list).forEach((methods) => {
        const found = methods.find((m) => m.tag === 0);
        if (found) lastUsed = found;
      });

      // 如果找到了最近使用的渠道，直接选中它, 如果不是选推荐的一个渠道
      if (lastUsed) {
        setMethod(lastUsed);
      } else {
        const firstCategory = groupKeys[0];
        const firstMethod = currentGroups[firstCategory]?.[0];
        if (firstMethod) {
          setMethod(firstMethod);
        }
      }
    }
  });

  // 处理方法选择
  const handleMethodSelect = (selectedMethod: PaymentMethod) => {
    setIsManualList(false);
    setMethod(selectedMethod);
  };

  /** Sync current selection via ?channelId= when returning from "More Channels" modal */
  createEffect(
    on(
      () => [props.isWithdraw, props.currency, searchParams.channelId, groupedMethods(), data()] as const,
      () => {
        if (!props.isWithdraw) return;
        const raw = searchParams.channelId;
        if (raw === undefined || raw === null || `${raw}` === '') return;
        const id = Number(raw);
        if (!Number.isFinite(id)) return;

        const cur = method();
        if (cur && Number(cur.channelId) === id) {
          const next = { ...searchParams } as Record<string, string | undefined>;
          delete next.channelId;
          setSearchParams(next as any, { replace: true });
          return;
        }

        const groups = groupedMethods();
        if (Object.keys(groups).length === 0) return;

        let found: PaymentMethod | undefined;
        for (const arr of Object.values(groups)) {
          found = arr.find((m) => Number(m.channelId) === id);
          if (found) break;
        }
        if (found) {
          handleMethodSelect(found);
          const next = { ...searchParams } as Record<string, string | undefined>;
          delete next.channelId;
          setSearchParams(next as any, { replace: true });
        }
      }
    )
  );

  const handleBackToList = () => {
    setIsManualList(true);
    setMethod(null);
  };

  // 格式化ETA时间
  const formatETA = (expiredTime: number) => {
    return Math.floor(expiredTime / 60).toFixed(1);
  };

  const formatFiatCurrency = (item: any) => {
    const minLimit = formatNumber(item.minLimit);
    const maxLimit = formatNumber(item.maxLimit);
    const ret = `${minLimit} ~ ${maxLimit}`;
    return (
      <div class={`w-0 ellipsis flex-auto text-right text-xs`} title={ret}>
        {ret}
      </div>
    );
  };

  const methodListView = (methods: PaymentMethod[]) => {
    return (
      <ul class={`p-2 rounded-lg space-y-2`} role="list">
        <For each={methods}>
          {(item) => (
            <li
              class={`relative bg-menu-bg-header-bg rounded-md p-1 cursor-pointer w-full flex items-center h-11  gap-4`}
              onClick={() => handleMethodSelect(item)}
              tabindex="0"
              role="button"
            >
              {item.category === 'Recommend' && (
                <div class="absolute -top-1 -left-0.5  h-4">
                  {item.tag === 0 ? <FlagGreen class="w-full h-full" /> : <FlagPurple class="w-full h-full" />}
                  <div
                    class="w-full h-full text-neutral-main1 px-1 text-[0.6rem]  absolute text-center left-[50%] right-[50%] top-[50%] bottom-[50%] -translate-x-[50%] -translate-y-[50%] overflow-hidden text-ellipsis text-nowrap"
                    title={item.tag === 0 ? t('Last Used') : t('Recommend')}
                  >
                    {item.tag === 0 ? t('Last Used') : t('Recommend')}
                  </div>
                </div>
              )}
              <div class="text-neutral-main1 ml-2 font-semibold">{item.displayName}</div>
              {formatFiatCurrency(item)}
              <div class="flex-none -mr-4 text-xs">ETA: {formatETA(item.expiredTime)}min</div>
              <img class="max-w-[3.7rem] max-h-[1.75rem]" src={item.lightIconUrl} alt="" srcset="" />
              <IconArrow06 class="-rotate-90 size-5" aria-hidden="true" />
            </li>
          )}
        </For>
      </ul>
    );
  };

  return (
    <section class="space-y-3" ref={ref}>
      {props.header}
      <Show
        when={method()}
        fallback={
          <Suspense fallback={<BarLoading class="mx-auto my-10 h-60" />}>
            <Show when={Object.keys(groupedMethods()).length} fallback={<Empty />}>
              <For each={Object.keys(groupedMethods())}>
                {(categoryKey) => {
                  let ulRef!: HTMLUListElement;
                  const getUlRef = () => ulRef;
                  // Memoize listData so it updates when groupedMethods changes
                  const listData = createMemo(() => groupedMethods()[categoryKey]);
                  function renderMethodsIcon() {
                    const uniqueIconList = createMemo(() => {
                      const ret = listData().map((item) => item.lightIconUrl);
                      return [...new Set(ret)];
                    });
                    return (
                      <For each={uniqueIconList()}>
                        {(item) =>
                          ![
                            'https://img2.dogcrash.game/fait/af/85/fa/173699158050968.png',
                            'https://img2.dogcrash.game',
                          ].includes(item) && <img class="max-w-[3.7rem] max-h-[1.75rem]" src={item} />
                        }
                      </For>
                    );
                  }
                  const methodTitle = categoryKey === 'Recommend' ? t('Recommend') : categoryKey;
                  return (
                    <Box title={methodTitle} class="!p-0">
                      <ToggleList items={listData().slice(0, 1)} showCount={3} ulRef={getUlRef}>
                        {(list) => methodListView(list())}
                      </ToggleList>
                    </Box>
                  );
                }}
              </For>
            </Show>
          </Suspense>
        }
      >
        {(selectedMethod) => {
          return (
            <>
              {/* 具体的充值或者体现表单 */}
              {props.isWithdraw
                ? (props as WithdrawPaymentProps).children(
                    selectedMethod() as WithdrawMethod,
                    handleBackToList,
                    groupedMethods,
                    handleMethodSelect
                  )
                : (props as DepositPaymentProps).children(
                    selectedMethod() as DepositMethod,
                    handleBackToList,
                    groupedMethods,
                    handleMethodSelect
                  )}
            </>
          );
        }}
      </Show>
    </section>
  );
}

export default Payment;
